import { useEffect, useRef, useState } from 'react';

// Background music track path (under `apps/web/public`, served from the
// site root). Looped, low-volume so it sits behind the page audio.
const MUSIC_SRC = '/sounds/ilovecooking-webbg.wav';
const MUSIC_VOLUME = 0.4;

// Header music toggle. Lives to the left of `LanguageSwitcher` in both
// the desktop and mobile branches of `Header.tsx`. Starts MUTED because
// modern browsers block autoplay of media with sound until the user
// interacts with the page — the first click on this button is what
// satisfies that gesture and starts playback.
export default function MusicToggle() {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Construct the audio element once on mount and reuse it across
  // toggles. Using a ref (not state) avoids re-renders when the audio's
  // internal state (currentTime, etc.) changes. Cleanup pauses + drops
  // the ref so HMR / unmount doesn't leave a phantom track playing.
  useEffect(() => {
    const audio = new Audio(MUSIC_SRC);
    audio.loop = true;
    audio.volume = MUSIC_VOLUME;
    audio.preload = 'auto';
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    // `play()` returns a Promise that rejects when autoplay policy
    // blocks it (shouldn't happen here since we're inside a click
    // handler, but stay defensive — leaving `playing` false if the
    // play attempt was rejected so the icon stays accurate).
    audio.play().then(
      () => setPlaying(true),
      () => setPlaying(false),
    );
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={playing ? 'Mute background music' : 'Play background music'}
      aria-pressed={playing}
      // Sizing mirrors `LanguageSwitcher` (h-8 mobile, h-[50px] lg) so
      // the two header chrome pieces sit on the same baseline.
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center transition hover:scale-110 active:scale-95 lg:h-[50px] lg:w-[50px]"
    >
      <img
        src={playing ? '/images/music_enable.png' : '/images/music_disable.png'}
        alt=""
        aria-hidden="true"
        className="h-full w-full select-none object-contain"
        draggable={false}
      />
    </button>
  );
}
